/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { captureException } from 'embercom/lib/sentry';
import { task } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import type Router from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import { taskFor } from 'ember-concurrency-ts';
import { showNewMessageInIntercomWidget } from 'embercom/lib/intercom-widget-helper';

export default class BillingIndexController extends Controller {
  @service declare store: Store;
  @service declare appService: $TSFixMe;
  @service declare customerService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare router: Router;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;

  queryParams = [];

  get featuresLost() {
    let prefix = 'new-settings.workspace.billing.cancel.confirm.lost-feature-';
    let featureRange = this.appService.app.onFinForPlatforms ? [4, 5, 6] : [1, 2, 3];

    return featureRange.map((num) => this.intl.t(`${prefix}${num}`));
  }

  get product() {
    if (this.appService.app.onFinForPlatforms) {
      return this.intl.t('new-settings.workspace.billing.product-fin');
    }
    return this.intl.t('new-settings.workspace.billing.product');
  }

  get isSalesforceContractedOrCustomPricing() {
    return (
      this.appService.app.isSalesforceContracted || this.customerService.customer.hasCustomPricing
    );
  }

  @action
  openMessenger() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'contact_us_cancel_subscription_button',
      place: 'settings.subscription.billing.cancel.confirm',
      section: 'cancel.confirm_screen',
      context: 'billing_settings',
    });
    showNewMessageInIntercomWidget(
      this.intl.t(
        'new-settings.workspace.billing.cancel.confirm.custom-pricing-or-salesforce-contracted',
      ),
    );
  }

  @action
  trackReturnButtonClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'return_to_billing_settings_button',
      place: 'settings.subscription.billing.cancel.confirm',
      section: 'cancel.confirm_screen',
      context: 'billing_settings',
    });
  }

  @task *cancelSubscriptionTask(): Generator<Promise<void>> {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'cancel_my_subscription_button',
      place: 'settings.subscription.billing.cancel.confirm',
      section: 'cancel.confirm_screen',
      context: 'billing_settings',
    });
    try {
      let newCustomer = yield ajax({
        url: `/ember/customers/${this.customerService.customer.id}/cancel_subscription`,
        type: 'POST',
        data: JSON.stringify({
          app_id: this.appService.app.id,
        }),
      });
      return this.store.pushPayload('billing/customer', {
        'billing/customer': newCustomer,
      });
    } catch (err) {
      this.notificationsService.notifyError(
        this.intl.t('new-settings.workspace.billing.cancel.confirm.error'),
      );
      captureException(err, {
        'responsible-team': 'team-monetization',
      });
      throw err;
    }
  }

  get isCancelSubscriptionRunning() {
    return taskFor(this.cancelSubscriptionTask).isRunning;
  }

  set isCancelSubscriptionRunning(_value: $TSFixMe) {
    // no-op
  }

  @action
  async cancelSubscription() {
    await taskFor(this.cancelSubscriptionTask).perform();
    this.router.transitionTo('apps.app.settings.subscription.billing.cancel.complete');
  }
}
